import { supabase } from '../lib/supabase'
import { supabaseError } from '../lib/errors'
import type { HandlingStatus, InboxAssignee, InboxItemType } from '../types/inbox'

export interface AssignInboxItemInput {
  itemType: InboxItemType
  itemId: string
  assigneeUserId: string | null
}

export interface SetHandlingStatusInput {
  itemType: InboxItemType
  itemId: string
  handlingStatus: HandlingStatus
  waitingUntil?: string | null
}

const CONVO_FIELDS =
  'id, assigned_to_user_id, assigned_at, assigned_by_user_id, handling_status, waiting_until, resolved_at, resolved_by, priority, queue_id, sla_due_at, state'

const CALL_FIELDS =
  'id, assigned_to_user_id, assigned_at, assigned_by_user_id, handling_status, waiting_until, resolved_at, resolved_by, priority, queue_id, sla_due_at, state'

export async function fetchInboxAssignees(): Promise<InboxAssignee[]> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, first_name, last_name, email, role')
    .eq('is_active', true)
    .order('first_name', { ascending: true })
  if (error) throw supabaseError(error, 'Failed to load assignees')
  return (data || []) as InboxAssignee[]
}

export async function assignInboxItem(input: AssignInboxItemInput): Promise<void> {
  const table = input.itemType === 'conversation' ? 'conversations' : 'phone_call_logs'
  const patch: Record<string, unknown> = {
    assigned_to_user_id: input.assigneeUserId,
    assigned_at: input.assigneeUserId ? new Date().toISOString() : null,
    assigned_by_user_id: input.assigneeUserId ? undefined : null,
  }
  if (input.assigneeUserId) {
    patch.handling_status = 'assigned'
  } else {
    patch.handling_status = 'open'
  }
  const { error } = await supabase.from(table).update(patch).eq('id', input.itemId)
  if (error) throw supabaseError(error, 'Failed to assign inbox item')
}

export async function setInboxHandlingStatus(input: SetHandlingStatusInput): Promise<void> {
  const table = input.itemType === 'conversation' ? 'conversations' : 'phone_call_logs'
  const patch: Record<string, unknown> = {
    handling_status: input.handlingStatus,
  }
  if (input.handlingStatus === 'waiting') {
    if (!input.waitingUntil) throw new Error('waiting_until is required for Waiting status')
    patch.waiting_until = input.waitingUntil
  } else {
    patch.waiting_until = null
  }
  if (input.handlingStatus === 'resolved') {
    patch.resolved_at = new Date().toISOString()
  } else {
    patch.resolved_at = null
    patch.resolved_by = null
  }
  const { error } = await supabase.from(table).update(patch).eq('id', input.itemId)
  if (error) throw supabaseError(error, 'Failed to update handling status')
}

export async function claimInboxItem(itemType: InboxItemType, itemId: string, userId: string): Promise<void> {
  await assignInboxItem({ itemType, itemId, assigneeUserId: userId })
}

export async function getInboxItemAssignment(itemType: InboxItemType, itemId: string) {
  const table = itemType === 'conversation' ? 'conversations' : 'phone_call_logs'
  const fields = itemType === 'conversation' ? CONVO_FIELDS : CALL_FIELDS
  const { data, error } = await supabase.from(table).select(fields).eq('id', itemId).single()
  if (error) throw supabaseError(error, 'Failed to load inbox item')
  return data
}

export async function bulkAssignInboxItems(
  items: Array<{ itemType: InboxItemType; itemId: string }>,
  assigneeUserId: string | null
): Promise<void> {
  for (const item of items) {
    await assignInboxItem({ ...item, assigneeUserId })
  }
}

export async function suggestAssigneeForCustomer(customerId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('customers')
    .select('assigned_to')
    .eq('id', customerId)
    .single()
  if (error) return null
  return data?.assigned_to || null
}
